import React, { useState, useEffect } from 'react';
import { Product, ProductSize, UpdateProductDto, Category, Subcategory, HttpError } from '@/domain/entities';
import { ImageModal } from './ImageModal';
import { AvailabilityOptionsEditor } from './AvailabilityOptionsEditor';
import { productService, categoryService, subcategoryService, uploadService } from '@/application/services';
import { ColorSelect } from './ColorSelect';
import { SHOE_SIZES, CLOTHING_SIZES, ONE_SIZE } from '@/domain/constants/sizes';

interface ProductPreviewProps {
  product: Product | null;
  sizes: ProductSize[];
  onClose: () => void;
  onUpdate?: () => void;
}

export const ProductPreview: React.FC<ProductPreviewProps> = ({
  product,
  sizes: initialSizes,
  onClose,
  onUpdate,
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [uploadingMainImage, setUploadingMainImage] = useState(false);
  const [uploadingGalleryImage, setUploadingGalleryImage] = useState(false);
  const [isOneSize, setIsOneSize] = useState(false);

  const [formData, setFormData] = useState({
    name: product?.name || '',
    brand: product?.brand || '',
    color: product?.color || '',
    gender: product?.gender || '',
    condition: product?.condition || '',
    year: product?.year || 2025,
    description: product?.description || '',
    categoryId: product?.categoryId || 1,
    subcategoryId: product?.subcategoryId || null,
  });

  const [sizes, setSizes] = useState<Omit<ProductSize, 'id' | 'sku' | 'createdAt' | 'updatedAt'>[]>([]);

  const [imageUrl, setImageUrl] = useState<string>(product?.imageUrl || '');
  const [images, setImages] = useState<string[]>(product?.images || []);

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        brand: product.brand || '',
        color: product.color || '',
        gender: product.gender || '',
        condition: product.condition || '',
        year: product.year || 2025,
        description: product.description || '',
        categoryId: product.categoryId || 1,
        subcategoryId: product.subcategoryId || null,
      });
      setImageUrl(product.imageUrl || '');
      setImages(product.images || []);
    }
    categoryService.getCategories().then(setCategories).catch(console.error);
  }, [product]);

  // Загружаем подкатегории при изменении категории
  useEffect(() => {
    const selectedCategory = categories.find(cat => cat.id === formData.categoryId);
    if (selectedCategory?.hasSubcategories) {
      subcategoryService.getSubcategories(formData.categoryId)
        .then(setSubcategories)
        .catch(console.error);
    } else {
      setSubcategories([]);
      setFormData(prev => ({ ...prev, subcategoryId: null }));
    }
  }, [formData.categoryId, categories]);

  useEffect(() => {
    if (initialSizes && initialSizes.length > 0) {
      setSizes(
        initialSizes.map((s) => ({
          us: s.us,
          price: s.price,
          discount: s.discount,
          inStock: s.inStock,
          quantity: s.quantity ?? 0,
          isPreOrder: s.isPreOrder ?? false,
          deliveryDays: s.deliveryDays ?? undefined,
          availabilityOptions: s.availabilityOptions || [], // Данные должны быть мигрированы в БД
        }))
      );
      // Проверяем, есть ли ONE SIZE
      if (initialSizes.length === 1 && initialSizes[0].us === ONE_SIZE) {
        setIsOneSize(true);
      }
    } else {
      setSizes([]);
    }
  }, [initialSizes]);

  if (!product) return null;

  const allImages = imageUrl
    ? [imageUrl, ...images]
    : images;

  const currentImage = allImages[selectedImageIndex] || imageUrl;

  const handleMainImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingMainImage(true);
    try {
      const { url } = await uploadService.uploadImage(file, 'products');
      setImageUrl(url);
      setSelectedImageIndex(0);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message || 'Не удалось загрузить изображение');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Не удалось загрузить изображение');
      }
    } finally {
      setUploadingMainImage(false);
      e.target.value = '';
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingGalleryImage(true);
    try {
      const { url } = await uploadService.uploadImage(file, 'products');
      setImages((prev) => [...prev, url]);
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message || 'Не удалось загрузить изображение');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Не удалось загрузить изображение');
      }
    } finally {
      setUploadingGalleryImage(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    if (index === 0 && imageUrl) {
      // Нельзя удалить основное изображение через эту функцию
      return;
    }
    const imageIndex = imageUrl ? index - 1 : index;
    setImages(images.filter((_, i) => i !== imageIndex));
    if (selectedImageIndex >= allImages.length - 1) {
      setSelectedImageIndex(Math.max(0, allImages.length - 2));
    }
  };

  const handlePreviousImage = () => {
    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : allImages.length - 1));
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev < allImages.length - 1 ? prev + 1 : 0));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Валидация всех размеров
    for (let i = 0; i < sizes.length; i++) {
      const validationError = validateSize(sizes[i]);
      if (validationError) {
        setError(`Ошибка в размере #${i + 1}: ${validationError}`);
        setLoading(false);
        return;
      }
    }

    // Автоматически вычисляем inStock на основе quantity и isPreOrder
    const sizesWithInStock = sizes.map(size => ({
      ...size,
      inStock: size.isPreOrder || (size.quantity ?? 0) > 0
    }));

    try {
      const updateData: UpdateProductDto = {
        ...formData,
        imageUrl,
        images,
        sizes: sizesWithInStock,
      };
      await productService.updateProduct(product.sku, updateData);
      if (onUpdate) {
        onUpdate();
      }
      onClose();
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message);
      } else {
        setError('Не удалось сохранить товар');
      }
    } finally {
      setLoading(false);
    }
  };

  // Определяем, какие размеры использовать в зависимости от категории
  const isClothingOrAccessories = formData.categoryId === 2 || formData.categoryId === 3; // 2 - Одежда, 3 - Аксессуары
  const availableSizes = isClothingOrAccessories ? CLOTHING_SIZES : SHOE_SIZES;

  const addSize = () => {
    if (isOneSize) {
      // Если ONE SIZE, добавляем только один размер
      setSizes([{ us: ONE_SIZE, price: 0, discount: null, inStock: true, quantity: 0, isPreOrder: false, availabilityOptions: [] }]);
    } else {
      setSizes([...sizes, { us: '', price: 0, discount: null, inStock: true, quantity: 0, isPreOrder: false, availabilityOptions: [] }]);
    }
  };

  const updateSize = (index: number, field: keyof ProductSize, value: any) => {
    setSizes(prevSizes => {
      const newSizes = [...prevSizes];
      newSizes[index] = { ...newSizes[index], [field]: value };
      return newSizes;
    });
  };

  // 🆕 Метод для обновления availability options
  const updateAvailabilityOptions = (index: number, options: any[]) => {
    setSizes(prevSizes => {
      const newSizes = [...prevSizes];
      newSizes[index] = { ...newSizes[index], availabilityOptions: options };
      return newSizes;
    });
  };

  // Обработка изменения чекбокса ONE SIZE
  const handleOneSizeChange = (checked: boolean) => {
    setIsOneSize(checked);
    if (checked) {
      // Если включен ONE SIZE, устанавливаем один размер
      setSizes([{ us: ONE_SIZE, price: 0, discount: null, inStock: true, quantity: 0, isPreOrder: false }]);
    } else {
      // Если выключен, очищаем размеры
      setSizes([]);
    }
  };

  // Валидация размера
  const validateSize = (size: Omit<ProductSize, 'id' | 'sku' | 'createdAt' | 'updatedAt'>): string | null => {
    if (size.quantity < 0) {
      return "Количество не может быть отрицательным";
    }
    
    if (size.isPreOrder && (!size.deliveryDays || size.deliveryDays <= 0)) {
      return "Для товаров под заказ укажите срок доставки";
    }
    
    // Разрешаем создавать товары "не в наличии" (quantity=0, isPreOrder=false)
    
    return null;
  };

  const removeSize = (index: number) => {
    setSizes(sizes.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4 pointer-events-none fade-in bg-black/50">
      <div 
        className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto border border-gray-300 dark:border-gray-700 pointer-events-auto zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Редактировать товар</h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <form id="product-edit-form" onSubmit={handleSubmit}>
          <div className="p-6">
            {error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
                {error}
              </div>
            )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Изображения */}
            <div>
              {/* Основное изображение с навигацией */}
              <div className="relative mb-4 group">
                {allImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handlePreviousImage();
                      }}
                      className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-gray-700 bg-opacity-90 dark:bg-opacity-90 text-gray-900 dark:text-white rounded-full w-10 h-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-100 dark:hover:bg-opacity-100 shadow-lg border border-gray-200 dark:border-gray-600"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleNextImage();
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white dark:bg-gray-700 bg-opacity-90 dark:bg-opacity-90 text-gray-900 dark:text-white rounded-full w-10 h-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-opacity-100 dark:hover:bg-opacity-100 shadow-lg border border-gray-200 dark:border-gray-600"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}
                <img
                  src={currentImage}
                  alt={product.name}
                  className="w-full h-auto rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowImageModal(true);
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f3f4f6" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="20"%3EИзображение отсутствует%3C/text%3E%3C/svg%3E';
                  }}
                />
                {allImages.length > 1 && (
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                    {selectedImageIndex + 1} / {allImages.length}
                  </div>
                )}
              </div>

              {/* Изображения */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Изображения <span className="text-red-500">*</span>
                </label>
                {(uploadingMainImage || uploadingGalleryImage) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {uploadingMainImage
                      ? 'Загрузка основного изображения...'
                      : 'Загрузка изображения...'}
                  </p>
                )}
                {/* Миниатюры всех изображений */}
                <div className="grid grid-cols-5 gap-2">
                  {/* Основное изображение */}
                  {imageUrl ? (
                    <div className="relative group">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setSelectedImageIndex(0);
                        }}
                        className={`w-full aspect-square rounded border-2 overflow-hidden ${
                            selectedImageIndex === 0
                              ? 'border-indigo-500 dark:border-indigo-400 ring-2 ring-indigo-200 dark:ring-indigo-800'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <img
                          src={imageUrl}
                          alt="Основное изображение"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </button>
                      <div className="absolute top-1 left-1 bg-indigo-600 text-white text-xs px-1.5 py-0.5 rounded font-semibold">
                        Основное
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setImageUrl('');
                          if (selectedImageIndex === 0 && images.length > 0) {
                            setSelectedImageIndex(0);
                          } else if (images.length === 0) {
                            setSelectedImageIndex(0);
                          }
                        }}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                      <label 
                        className={`absolute bottom-1 left-1 right-1 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded cursor-pointer hover:bg-opacity-70 transition-opacity opacity-0 group-hover:opacity-100 ${uploadingMainImage ? 'opacity-70 cursor-wait pointer-events-none' : ''}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          disabled={uploadingMainImage}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleMainImageUpload(e);
                          }}
                          className="hidden"
                        />
                        Заменить
                      </label>
                    </div>
                  ) : (
                    <label 
                      className={`aspect-square w-full rounded border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-indigo-400 dark:hover:border-indigo-500 cursor-pointer flex flex-col items-center justify-center gap-1 transition-colors bg-gray-50 dark:bg-gray-700 ${uploadingMainImage ? 'opacity-60 cursor-wait pointer-events-none' : ''}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          e.stopPropagation();
                          handleMainImageUpload(e);
                        }}
                        className="hidden"
                      />
                      <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-xs text-gray-500 dark:text-gray-400 text-center px-2">Основное</span>
                    </label>
                  )}
                  {/* Дополнительные изображения */}
                  {images.map((img, index) => {
                    const imageIndex = imageUrl ? index + 1 : index;
                    return (
                      <div key={index} className="relative group">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setSelectedImageIndex(imageIndex);
                          }}
                          className={`w-full aspect-square rounded border-2 overflow-hidden ${
                            selectedImageIndex === imageIndex
                              ? 'border-indigo-500 dark:border-indigo-400 ring-2 ring-indigo-200 dark:ring-indigo-800'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                        >
                          <img
                            src={img}
                            alt={`${product.name} ${imageIndex + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleRemoveImage(imageIndex);
                          }}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                  {/* Кнопка добавления дополнительного изображения */}
                  <label 
                    className={`aspect-square w-full rounded border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-indigo-400 dark:hover:border-indigo-500 cursor-pointer flex flex-col items-center justify-center gap-2 transition-colors bg-gray-50 dark:bg-gray-700 ${uploadingGalleryImage ? 'opacity-60 cursor-wait pointer-events-none' : ''}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      disabled={uploadingGalleryImage}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleImageUpload(e);
                      }}
                      className="hidden"
                    />
                    <span className="text-2xl text-gray-400 dark:text-gray-500">+</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 text-center px-2">Загрузить</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Информация */}
            <div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">SKU</label>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">{product.sku}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">ID</label>
                  <p className="text-gray-900 dark:text-white">{product.id}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Название *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Бренд *</label>
                    <input
                      type="text"
                      required
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Валюта</label>
                    <p className="text-gray-900 dark:text-white py-2">{product.currency}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Категория</label>
                    <select
                      value={formData.categoryId}
                      onChange={(e) =>
                        setFormData({ ...formData, categoryId: Number(e.target.value), subcategoryId: null })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.nameRu}
                        </option>
                      ))}
                    </select>
                  </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Пол</label>
                      <select
                        value={formData.gender}
                        onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="Unisex">Unisex</option>
                        <option value="Man">Man</option>
                        <option value="Women">Women</option>
                      </select>
                    </div>
                </div>

                {subcategories && subcategories.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Подкатегория</label>
                    <select
                      value={formData.subcategoryId || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, subcategoryId: e.target.value ? Number(e.target.value) : null })
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="">Не выбрано</option>
                      {subcategories.map((subcat) => (
                        <option key={subcat.id} value={subcat.id}>
                          {subcat.nameRu}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Цвет</label>
                    <ColorSelect
                      value={formData.color}
                      onChange={(color) => setFormData({ ...formData, color })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Состояние</label>
                    <input
                      type="text"
                      value={formData.condition}
                      onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Год</label>
                    <input
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">В избранном</label>
                    <p className="text-gray-900 dark:text-white py-2">{product.favorites} раз</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">Описание</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Размеры - на всю ширину */}
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block">
                Размеры и цены
              </label>
              <div className="flex items-center gap-4">
                {isClothingOrAccessories && (
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isOneSize}
                      onChange={(e) => handleOneSizeChange(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 bg-white dark:bg-gray-700"
                    />
                    <span>ONE SIZE</span>
                  </label>
                )}
                <button
                  type="button"
                  onClick={addSize}
                  disabled={isOneSize && sizes.length > 0}
                  className={`text-sm ${isOneSize && sizes.length > 0 ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300'}`}
                >
                  + Добавить размер
                </button>
              </div>
            </div>
            {sizes.length > 0 ? (
              <div className="space-y-4">
                {sizes.map((size, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
                    {/* Шапка: Размер слева + Кнопка удалить справа */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Размер:
                        </label>
                        {isOneSize ? (
                          <input
                            type="text"
                            value={ONE_SIZE}
                            disabled
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed w-32"
                          />
                        ) : (
                          <select
                            value={size.us}
                            onChange={(e) => updateSize(index, 'us', e.target.value)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 w-32"
                          >
                            <option value="">Выбрать</option>
                            {availableSizes.map((availableSize) => (
                              <option key={availableSize} value={availableSize}>
                                {availableSize}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      
                      {/* Кнопка удалить справа */}
                      <button
                        type="button"
                        onClick={() => removeSize(index)}
                        disabled={isOneSize}
                        className={`px-3 py-1 rounded text-sm font-medium ${
                          isOneSize 
                            ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed bg-gray-100 dark:bg-gray-700' 
                            : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-300 dark:border-red-700'
                        }`}
                      >
                        Удалить
                      </button>
                    </div>
                    
                    {/* Способы доставки на всю ширину */}
                    <AvailabilityOptionsEditor
                      options={size.availabilityOptions || []}
                      onChange={(options) => updateAvailabilityOptions(index, options)}
                      disabled={false}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">Нет размеров</p>
            )}
          </div>
        </div>
        </form>

        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Отмена
          </button>
          <button
            type="submit"
            form="product-edit-form"
            disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </div>

      {showImageModal && currentImage && (
        <ImageModal
          imageUrl={currentImage}
          alt={product.name}
          images={allImages}
          initialIndex={selectedImageIndex}
          onClose={() => setShowImageModal(false)}
        />
      )}
    </div>
  );
};

